import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import IconButton from '../components/IconButton';
import './Appointments.css';

moment.locale('de');
const localizer = momentLocalizer(moment);

function Appointments({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [showListView, setShowListView] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [canHandleAppointments, setCanHandleAppointments] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    if (tenantId) {
      loadUserInfo();
      loadAppointments();
      loadCustomers();
      loadCars();
      loadUsers();
      generateIcalUrl();
    }
  }, [tenantId]);
  
  const loadUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || '/api'}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setCanHandleAppointments(userData.canHandleAppointments !== false);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  useEffect(() => {
    if (tenantId) {
      loadAppointments();
    }
  }, [date, tenantId]);

  const generateIcalUrl = () => {
    const token = localStorage.getItem('token');
    if (token && tenantId) {
      const baseUrl = config.API_URL || 'https://api.garagedesk.net/api';
      setIcalUrl(`${baseUrl}/appointments/ical`);
    }
  };

  const loadAppointments = async () => {
    try {
      const startDate = new Date(date);
      if (view === 'month') {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
      } else if (view === 'week') {
        const day = startDate.getDay();
        const diff = startDate.getDate() - day;
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);
      } else {
        startDate.setHours(0, 0, 0, 0);
      }
      
      const endDate = new Date(startDate);
      if (view === 'month') {
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
      } else if (view === 'week') {
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
      } else {
        endDate.setHours(23, 59, 59, 999);
      }
      
      const data = await api.get(
        `/appointments?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        tenantId
      );
      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const tenantUsers = data.filter(u => u.tenantId === tenantId);
        setUsers(tenantUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleConfirm = async (id) => {
    if (!canHandleAppointments) {
      showNotification('Sie sind nicht berechtigt, Termine zu bestätigen', 'error');
      return;
    }
    try {
      await api.put(`/appointments/${id}/confirm`, {}, tenantId);
      setSelectedAppointment(null);
      loadAppointments();
      showNotification('Termin erfolgreich bestätigt', 'success');
    } catch (error) {
      console.error('Error confirming appointment:', error);
      const errorMsg = error.response?.data?.error || 'Fehler beim Bestätigen des Termins';
      showNotification(errorMsg, 'error');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Termin wirklich absagen?')) {
      return;
    }
    try {
      await api.put(`/appointments/${id}/cancel`, {}, tenantId);
      setSelectedAppointment(null);
      loadAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      showNotification('Fehler beim Absagen des Termins', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Termin wirklich löschen?')) {
      return;
    }
    try {
      await api.delete(`/appointments/${id}`, tenantId);
      setSelectedAppointment(null);
      loadAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showNotification('Fehler beim Löschen des Termins', 'error');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : '';
  };

  const getCarInfo = (carId) => {
    const car = cars.find(c => c.id === carId);
    return car ? `${car.make} ${car.model}` : '';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      cancelled: 'Abgesagt'
    };
    return labels[status] || status;
  };

  const getEventStyle = (event) => {
    const baseStyle = {
      borderRadius: '4px',
      border: 'none',
      padding: '2px 4px',
      fontSize: '0.85em'
    };

    if (event.status === 'pending') {
      return {
        ...baseStyle,
        backgroundColor: '#ff9800',
        color: 'white'
      };
    } else if (event.status === 'confirmed') {
      return {
        ...baseStyle,
        backgroundColor: '#4caf50',
        color: 'white'
      };
    } else if (event.status === 'cancelled') {
      return {
        ...baseStyle,
        backgroundColor: '#f44336',
        color: 'white'
      };
    }
    return baseStyle;
  };

  const calendarEvents = appointments
    .filter(apt => apt.status !== 'cancelled')
    .map(apt => {
      const start = new Date(apt.dateTime);
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      const customerName = getCustomerName(apt.customerId);
      const carInfo = getCarInfo(apt.carId);
      
      return {
        id: apt.id,
        title: `${customerName}${carInfo ? ` - ${carInfo}` : ''}${apt.description ? `: ${apt.description}` : ''}`,
        start,
        end,
        status: apt.status,
        appointment: apt
      };
    });

  const handleSelectEvent = (event) => {
    const apt = event.appointment;
    setSelectedAppointment(apt);
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const copyIcalUrl = () => {
    navigator.clipboard.writeText(icalUrl).then(() => {
      showNotification('iCal-URL in Zwischenablage kopiert! Sie können diese URL in Outlook oder anderen Kalender-Apps abonnieren.', 'error');
    }).catch(err => {
      console.error('Fehler beim Kopieren:', err);
    });
  };

  return (
    <div className="appointments">
      <div className="page-header" style={{ marginBottom: '1rem' }}>
        <h1>Termine</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setShowListView(!showListView)}
            className="btn-secondary btn-small"
          >
            <i className={`fas ${showListView ? 'fa-calendar' : 'fa-list'}`} style={{ marginRight: '0.5rem' }}></i>
            {showListView ? 'Kalender-Ansicht' : 'Listen-Ansicht'}
          </button>
          {icalUrl && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={copyIcalUrl}
                className="btn-secondary btn-small"
                title="iCal-URL kopieren für Outlook/Google Calendar"
              >
                <i className="fas fa-calendar-plus" style={{ marginRight: '0.5rem' }}></i>
                iCal abonnieren
              </button>
              <a
                href={icalUrl}
                download="garagedesk-appointments.ics"
                className="btn-secondary btn-small"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                title="iCal-Datei herunterladen"
              >
                <i className="fas fa-download" style={{ marginRight: '0.5rem' }}></i>
                iCal herunterladen
              </a>
            </div>
          )}
        </div>
      </div>

      {showListView ? (
        <div className="appointments-list-view">
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="date"
              value={moment(date).format('YYYY-MM-DD')}
              onChange={(e) => setDate(new Date(e.target.value))}
              style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {appointments
              .filter(apt => moment(apt.dateTime).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD'))
              .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
              .map(apt => {
                const customer = customers.find(c => c.id === apt.customerId);
                const car = cars.find(c => c.id === apt.carId);
                return (
                  <div
                    key={apt.id}
                    onClick={() => navigate(`/appointments/${apt.id}`)}
                    style={{
                      padding: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: apt.status === 'pending' ? '#fff3e0' : apt.status === 'confirmed' ? '#e8f5e9' : '#ffebee',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = apt.status === 'pending' ? '#ffe0b2' : apt.status === 'confirmed' ? '#c8e6c9' : '#ffcdd2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = apt.status === 'pending' ? '#fff3e0' : apt.status === 'confirmed' ? '#e8f5e9' : '#ffebee'}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {moment(apt.dateTime).format('HH:mm')} - {getCustomerName(apt.customerId)}
                      </div>
                      {car && (
                        <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '0.25rem' }}>
                          {getCarInfo(apt.carId)}
                        </div>
                      )}
                      {apt.description && (
                        <div style={{ color: '#666', fontSize: '0.9em' }}>
                          {apt.description}
                        </div>
                      )}
                      <span className={`status-badge status-${apt.status}`} style={{ marginTop: '0.5rem', display: 'inline-block' }}>
                        {getStatusLabel(apt.status)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {customer && (
                        <button
                          type="button"
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/customers/${customer.id}`)}
                          title="Kundendetails anzeigen"
                        >
                          <i className="fas fa-user" style={{ marginRight: '0.25rem' }}></i>
                          Kunde
                        </button>
                      )}
                      {car && (
                        <button
                          type="button"
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/cars/${car.id}`)}
                          title="Fahrzeugdetails anzeigen"
                        >
                          <i className="fas fa-car" style={{ marginRight: '0.25rem' }}></i>
                          Auto
                        </button>
                      )}
                      {apt.status === 'pending' && canHandleAppointments && (
                        <button
                          type="button"
                          className="btn-small"
                          onClick={() => handleConfirm(apt.id)}
                          title="Termin bestätigen"
                        >
                          <i className="fas fa-check" style={{ marginRight: '0.25rem' }}></i>
                          Bestätigen
                        </button>
                      )}
                      {apt.status !== 'cancelled' && (
                        <button
                          type="button"
                          className="btn-small btn-danger"
                          onClick={() => apt.status === 'pending' ? handleCancel(apt.id) : handleDelete(apt.id)}
                          title={apt.status === 'pending' ? 'Termin absagen' : 'Termin löschen'}
                        >
                          <i className={`fas ${apt.status === 'pending' ? 'fa-times' : 'fa-trash'}`} style={{ marginRight: '0.25rem' }}></i>
                          {apt.status === 'pending' ? 'Absagen' : 'Löschen'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            {appointments.filter(apt => moment(apt.dateTime).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')).length === 0 && (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>Keine Termine für dieses Datum</p>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="appointments-calendar-view" style={{ height: '50vh', marginTop: '1rem' }}>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              view={view}
              date={date}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onSelectEvent={handleSelectEvent}
              views={['month', 'week', 'day', 'agenda']}
              messages={{
                next: 'Weiter',
                previous: 'Zurück',
                today: 'Heute',
                month: 'Monat',
                week: 'Woche',
                day: 'Tag',
                agenda: 'Agenda',
                date: 'Datum',
                time: 'Zeit',
                event: 'Termin',
                noEventsInRange: 'Keine Termine in diesem Zeitraum',
                showMore: total => `+${total} weitere`,
                allDay: 'Ganztägig',
                work_week: 'Arbeitswoche',
                yesterday: 'Gestern',
                tomorrow: 'Morgen'
              }}
              culture="de"
              eventPropGetter={getEventStyle}
              popup
            />
          </div>
          
          {selectedAppointment && (
            <div className="appointment-details-panel" style={{
              background: 'white',
              borderRadius: '4px',
              padding: '1.5rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              height: '45vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0 }}>Termin-Details</h2>
                <button
                  type="button"
                  onClick={() => setSelectedAppointment(null)}
                  className="btn-small btn-secondary"
                >
                  Schließen
                </button>
              </div>
              
              {(() => {
                const apt = selectedAppointment;
                const customer = customers.find(c => c.id === apt.customerId);
                const car = cars.find(c => c.id === apt.carId);
                
                return (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Datum & Zeit</div>
                      <div>{moment(apt.dateTime).format('DD.MM.YYYY HH:mm')} Uhr</div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Status</div>
                      <span className={`status-badge status-${apt.status}`}>
                        {getStatusLabel(apt.status)}
                      </span>
                    </div>
                    
                    {customer && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Kunde</div>
                        <div>
                          {customer.firstName} {customer.lastName}
                          <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={() => navigate(`/customers/${customer.id}`)}
                            style={{ marginLeft: '0.5rem' }}
                          >
                            Details
                          </button>
                        </div>
                        {customer.email && (
                          <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25rem' }}>
                            {customer.email}
                          </div>
                        )}
                        {customer.phone && (
                          <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25rem' }}>
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {car && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Fahrzeug</div>
                        <div>
                          {car.make} {car.model} {car.licensePlate && `(${car.licensePlate})`}
                          <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={() => navigate(`/cars/${car.id}`)}
                            style={{ marginLeft: '0.5rem' }}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {apt.description && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Beschreibung</div>
                        <div>{apt.description}</div>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => navigate(`/appointments/${apt.id}`)}
                      >
                        Vollständige Details
                      </button>
                      {apt.status === 'pending' && canHandleAppointments && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            handleConfirm(apt.id);
                            setSelectedAppointment(null);
                          }}
                        >
                          Bestätigen
                        </button>
                      )}
                      {apt.status !== 'cancelled' && (
                        <button
                          type="button"
                          className="btn-secondary btn-danger"
                          onClick={() => {
                            if (apt.status === 'pending') {
                              handleCancel(apt.id);
                            } else {
                              handleDelete(apt.id);
                            }
                            setSelectedAppointment(null);
                          }}
                        >
                          {apt.status === 'pending' ? 'Absagen' : 'Löschen'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Appointments;
